/** TanStack Query hooks for admin enrolment management (enrol/unenrol users in courses). */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';
import type { Enrolment, EnrolmentWithCourse, PaginatedResponse } from '@formency/shared';
import { adminUserKeys } from './useAdminUsers';
import { continueKeys } from './useContinue';

// Re-export for convenience
export type { EnrolmentWithCourse } from '@formency/shared';

// ─── Query key factory ──────────────────────────────────────────────────────

export const adminEnrolmentKeys = {
  all: ['f', 'admin-enrolments'] as const,
  userEnrolments: (userId: string, params?: { page?: number; pageSize?: number }) =>
    [...adminEnrolmentKeys.all, userId, params] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

/** Fetch paginated enrolments for a specific user (admin). */
export function useAdminUserEnrolments(
  userId: string,
  params?: { page?: number; pageSize?: number }
) {
  return useQuery<PaginatedResponse<EnrolmentWithCourse>>({
    queryKey: adminEnrolmentKeys.userEnrolments(userId, params),
    queryFn: () =>
      api.get<PaginatedResponse<EnrolmentWithCourse>>(
        `/users/${userId}/enrolments`,
        params as Record<string, unknown> | undefined
      ),
    enabled: !!userId,
    placeholderData: keepPreviousData,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Enrol a user in a course (admin). */
export function useAdminEnrolUserInCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, courseId }: { userId: string; courseId: string }) =>
      api.post<Enrolment>(`/users/${userId}/enrolments`, { courseId }),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: adminEnrolmentKeys.userEnrolments(userId) });
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: continueKeys.all });
      toast.success('User enrolled');
    },
    onError: () => {
      toast.error('Failed to enrol user');
    },
  });
}

/** Unenrol a user from a course (admin). */
export function useAdminUnenrolUserFromCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, courseId }: { userId: string; courseId: string }) =>
      api.del(`/users/${userId}/enrolments/${courseId}`),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: adminEnrolmentKeys.userEnrolments(userId) });
      qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: continueKeys.all });
      toast.success('User unenrolled');
    },
    onError: () => {
      toast.error('Failed to unenrol user');
    },
  });
}
